import React from "react";
import "./AppButton.scss";

/**
 * AppButton — the app's button. 1,100+ adopters.
 *
 * Props:
 *   variant   — "default" | "outline" | "secondary" | "ghost" | "destructive"
 *               | "outline-destructive" | "ghost-destructive" | "success"
 *   size      — "xs" | "sm" | "default" | "lg" | "icon" | "icon-sm" | "icon-xs"
 *
 * For an icon-only button reach for `IconButton`, which is this component with
 * the accessible name made a required prop.
 *   children  — button content
 *   className — extra class
 *   disabled  — disabled state
 *   onClick   — click handler
 *   as        — render as a different element (e.g. "a", Link)
 *
 * ── WHY THE ALIAS MAP EXISTS ────────────────────────────────────────────
 * This component's vocabulary is shadcn's (`default` / `destructive`);
 * react-bootstrap's `<Button>`, which 257 files also import, uses
 * `primary` / `danger` / `success`. The two are one import line apart and
 * the names were being crossed: measured 2026-08-25, **70 call sites asked
 * AppButton for a variant it does not implement** — 59 `primary`, 7
 * `danger`, 2 `outline-danger`, 1 `success`, 1 `outline-secondary` — plus
 * 6 asking for `size="xs"`.
 *
 * None of them errored. An unknown variant emitted `.app-btn--primary`,
 * which no rule matches, so the button fell back to bare `.app-btn`:
 * `border: 1px solid transparent`, no background, inherited ink. Seventy
 * buttons — including every primary action on the Attendance module — were
 * rendering as invisible text. An unknown SIZE was worse: the size blocks
 * are the only source of padding and min-height, so `xs` had neither.
 *
 * Renaming 70 call sites would fix today and not tomorrow, because the
 * next person to write `variant="primary"` is the same person who just
 * wrote it in react-bootstrap on the line above. So the aliases resolve
 * here, and `warnUnknown` makes the NEXT unknown name loud in development
 * instead of silent in production — the failure mode this whole programme
 * keeps finding.
 */

const VARIANTS = new Set([
  "default",
  "outline",
  // `outline` in the brand colour, for a control that is currently ENGAGED —
  // a filter trigger with filters applied, a toggled toolbar button. It is the
  // outline variant's twin, so the two stay the same button in two states
  // rather than a button and a hand-rolled override: recolouring `outline` from
  // a call site means a Tailwind utility racing `.app-btn--outline` on equal
  // specificity, decided by emitted source order.
  "outline-primary",
  "secondary",
  "ghost",
  "destructive",
  "outline-destructive",
  "ghost-destructive",
  "success",
]);
const SIZES = new Set(["xs", "sm", "default", "lg", "icon", "icon-sm", "icon-xs"]);

// react-bootstrap's vocabulary → ours. Deliberately NOT exhaustive: these are
// the five names call sites actually reached for, measured. Adding `link` or
// `light` "for completeness" would be inventing a semantic nobody asked for —
// and would silence the warning that is the point of the rest of this file.
const VARIANT_ALIASES = {
  primary: "default",
  danger: "destructive",
  "outline-danger": "outline-destructive",
  "outline-secondary": "outline",
};

const warned = new Set();
const warnUnknown = (kind, value, fallback) => {
  if (process.env.NODE_ENV === "production") return;
  const key = `${kind}:${value}`;
  if (warned.has(key)) return;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn(
    `[AppButton] unknown ${kind} "${value}" — falling back to "${fallback}". ` +
      `Valid ${kind}s: ${[...(kind === "variant" ? VARIANTS : SIZES)].join(", ")}.`
  );
};

const resolveVariant = (variant) => {
  if (VARIANTS.has(variant)) return variant;
  if (VARIANT_ALIASES[variant]) return VARIANT_ALIASES[variant];
  warnUnknown("variant", variant, "default");
  return "default";
};

const resolveSize = (size) => {
  if (SIZES.has(size)) return size;
  warnUnknown("size", size, "default");
  return "default";
};

const AppButton = React.forwardRef(
  (
    {
      variant = "default",
      size = "default",
      children,
      className = "",
      disabled = false,
      as: Comp = "button",
      ...props
    },
    ref
  ) => {
    const classes = [
      "app-btn",
      `app-btn--${resolveVariant(variant)}`,
      `app-btn--${resolveSize(size)}`,
      disabled ? "app-btn--disabled" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Comp ref={ref} className={classes} disabled={disabled} {...props}>
        {children}
      </Comp>
    );
  }
);

AppButton.displayName = "AppButton";
export default AppButton;
